import Modal from 'bootstrap/js/dist/modal';

/**
 * Session Renewal Widget
 *
 * Reads the JWT exp claim from the jwt_hp cookie and shows a modal
 * before the token expires. The user clicks "Continue" which fetches
 * the current page (main firewall) to refresh the JWT cookies.
 *
 * This prevents 401 errors on API calls when the JWT expires while
 * the PHP session (24h) is still alive.
 */

const WARN_BEFORE_SECONDS = 60; // show modal 60s before expiry
const CHECK_INTERVAL_MS   = 15000; // check every 15s

let timer       = null;
let modalEl     = null;
let modalInstance = null;
let isRenewing  = false;

function getJwtExp() {
    const cookie = document.cookie
        .split('; ')
        .find(c => c.startsWith('jwt_hp='));

    if (!cookie) return null;

    const value = cookie.substring(7); // "jwt_hp=".length
    const parts = value.split('.');
    if (parts.length < 2) return null;

    try {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.exp || null;
    } catch {
        return null;
    }
}

function getSecondsRemaining() {
    const exp = getJwtExp();
    if (!exp) return null;
    return exp - Math.floor(Date.now() / 1000);
}

function createModal() {
    if (modalEl) return;

    modalEl = document.createElement('div');
    modalEl.id = 'session-renewal-modal';
    modalEl.className = 'modal fade';
    modalEl.setAttribute('tabindex', '-1');
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.setAttribute('data-bs-backdrop', 'static');
    modalEl.setAttribute('data-bs-keyboard', 'false');

    modalEl.innerHTML = `
        <div class="modal-dialog modal-dialog-centered modal-sm">
            <div class="modal-content">
                <div class="modal-body text-center py-4">
                    <i class="fa-light fa-clock fa-2x text-warning mb-3 d-block"></i>
                    <p class="mb-3">${window.Syn.trans('session.expiring', {}, 'SyndustryCommonBundle')}</p>
                    <button type="button" class="btn btn-primary" id="session-renewal-btn">
                        ${window.Syn.trans('session.continue', {}, 'SyndustryCommonBundle')}
                    </button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modalEl);
    modalInstance = new Modal(modalEl, { backdrop: 'static', keyboard: false });

    modalEl.querySelector('#session-renewal-btn').addEventListener('click', renew);
}

function showSessionExpired() {
    const body = modalEl.querySelector('.modal-body');
    body.innerHTML = `
        <i class="fa-light fa-right-from-bracket fa-2x text-danger mb-3 d-block"></i>
        <p class="mb-3">${window.Syn.trans('session.expired', {}, 'SyndustryCommonBundle')}</p>
        <a href="${window.location.href}" class="btn btn-primary">
            ${window.Syn.trans('session.login', {}, 'SyndustryCommonBundle')}
        </a>
    `;
}

function showRetryState(btn) {
    btn.classList.replace('btn-primary', 'btn-warning');
    btn.innerHTML = window.Syn.trans('session.retry', {}, 'SyndustryCommonBundle');
}

async function renew() {
    if (isRenewing) return;
    isRenewing = true;

    const btn = modalEl.querySelector('#session-renewal-btn');
    btn.disabled = true;
    btn.innerHTML = '<i class="fa-light fa-spinner fa-spin"></i>';

    try {
        // Fetch current page on the main firewall — JWTSubscriber rewrites cookies.
        // We only need the Set-Cookie response headers, body is discarded.
        await fetch(window.location.href, {
            method: 'GET',
            headers: { 'Accept': 'text/html' },
            credentials: 'same-origin'
        });

        // Verify the cookie was actually refreshed.
        // If the PHP session also expired, the fetch redirected to login
        // and no new JWT cookie was set.
        const newRemaining = getSecondsRemaining();
        if (newRemaining === null || newRemaining <= 0) {
            showSessionExpired();
            return;
        }

        // Restart the timer for the fresh token
        stop();
        start();
        modalInstance.hide();
    } catch {
        // Show retry state — never reload, user may have unsaved data
        showRetryState(btn);
    } finally {
        btn.disabled = false;
        isRenewing = false;
    }
}

function check() {
    const remaining = getSecondsRemaining();

    // No cookie = not authenticated or cookie already gone
    if (remaining === null) {
        stop();
        return;
    }

    // Token already expired — show modal, don't reload (user may have unsaved data)
    if (remaining <= 0) {
        createModal();
        if (!modalEl.classList.contains('show')) {
            modalInstance.show();
        }
        return;
    }

    // Show warning modal
    if (remaining <= WARN_BEFORE_SECONDS) {
        createModal();
        if (!modalEl.classList.contains('show')) {
            modalInstance.show();
        }
    }
}

function start() {
    if (timer) return;
    // Only start if there's a JWT cookie (authenticated user)
    if (getJwtExp() === null) return;
    timer = setInterval(check, CHECK_INTERVAL_MS);
}

function stop() {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

/**
 * Force-show the renewal modal. For dev/testing only.
 * Usage: Syn.libs.SessionRenewal.showNow()
 */
function showNow() {
    createModal();
    modalInstance.show();
}

export default { start, stop, showNow };
